import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { RefCallBack, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '@tanstack/react-router';
import { CirclePlus, SearchIcon } from 'lucide-react';

import { openModal } from '@/components/modals/modals.utils';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';
import { Estate, Estates, getEstates } from '@/features/estates/api/estates';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import { getPriceWithCurrency } from '@/utils/getPriceWithCurrency';

interface ObjectRelationSettingsContentProps {
  closePopover: () => void;
  onChangeEstateId: (id: string) => void;
  setSelectedEstate: Dispatch<SetStateAction<Estate | undefined>>;
}

export const ObjectRelationSettingsContent = ({
  closePopover,
  onChangeEstateId,
  setSelectedEstate,
}: ObjectRelationSettingsContentProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [queriedEstates, setQueriedEstates] = useState<Estates>([]);

  const { register, watch } = useForm<{ searchString: string }>({
    defaultValues: { searchString: '' },
  });

  const searchString = watch('searchString');

  const getDebouncedEstates = useDebouncedCallback(async () => {
    try {
      setLoading(true);
      const estates = await getEstates({
        query: {
          ...(searchString
            ? {
                filters: {
                  searchString,
                },
              }
            : {}),
        },
      });

      setQueriedEstates(estates);
    } finally {
      setLoading(false);
    }
  });

  const { ref, ...restInputProps } = register('searchString');

  const inputRefCallbackHandler: RefCallBack = (e) => {
    ref(e);
    setInputRef(e);
  };

  const onAddObject = () => {
    openModal('create-estate', {
      navigate,
      onSuccess: (estateId) => {
        getDebouncedEstates();
        handleChangeEstate(estateId);
      },
    });
  };

  const handleChangeEstate = (estate: Estate | string) => {
    if (typeof estate === 'string') {
      onChangeEstateId(estate);
    } else if ('id' in estate) {
      onChangeEstateId(estate.id);
      setSelectedEstate(estate);
    }
    closePopover();
  };

  useEffect(() => {
    getDebouncedEstates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString]);

  useEffect(() => {
    if (inputRef) inputRef.focus();
  }, [inputRef]);

  return (
    <div className="w-full">
      <div className="flex h-[40px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6 sm:h-[60px]">
        <span className="text-sm font-semibold text-white">{t('object')}</span>
      </div>
      <div className="bg-white p-2 sm:px-5 sm:py-6">
        <div className="flex w-full max-w-screen-sm items-center gap-2">
          <Input
            {...restInputProps}
            ref={inputRefCallbackHandler}
            size="sm"
            placeholder={t('search')}
            className="border"
            startIcon={<SearchIcon className="text-primary-light" />}
          />
          <CirclePlus
            onClick={onAddObject}
            className="mx-auto size-10 cursor-pointer text-primary-light sm:mx-0"
          />
        </div>
        <div className="relative mt-3 flex h-screen max-h-calc-full-minus-100 items-center justify-center overflow-y-auto overflow-x-visible sm:max-h-96">
          {loading ? (
            <div className="absolute z-10 flex h-screen max-h-calc-full-minus-100 flex-col-reverse items-center justify-center bg-background sm:max-h-96">
              <Spinner className="size-12 [&_*]:stroke-primary-dark" />
            </div>
          ) : queriedEstates.length > 0 ? (
            <div className="flex h-full w-full flex-col gap-3">
              {queriedEstates.map((estate) => {
                return (
                  <Card
                    key={`estate-card${estate.id}`}
                    className="grid h-[105px] min-h-[105px] cursor-pointer grid-cols-[140px,auto] rounded-none"
                    role="listitem"
                    onClick={() => handleChangeEstate(estate)}
                  >
                    <div className="flex size-full overflow-hidden">
                      <EstateImage
                        className="size-full"
                        {...(estate.thumbnail?.mediaId && {
                          thumbnailId: estate.thumbnail?.mediaId,
                        })}
                      />
                    </div>
                    <div className="flex flex-1 flex-col pl-3">
                      <span className="max-w-[270px] truncate text-sm max-md:mb-2 max-sm:w-48">
                        {estate.name}
                      </span>
                      <span className="block max-w-[270px] truncate text-[10px] font-medium">
                        {`${getRenderValue(estate.address?.city)}, ${getRenderValue(estate.address?.address?.split(', ')?.[1])}, ${getRenderValue(estate.address?.postalCode)}, ${getRenderValue(estate.address?.city)}`}
                      </span>
                      <div className="mt-auto flex gap-3">
                        <div>
                          <span className="truncate text-[10px] font-medium leading-[28px] text-[#A0A0A0]">
                            {t('area')}
                          </span>
                          <span className="block truncate text-[10px] font-medium leading-[28px]">
                            {getRenderValue(estate.estateAreasRooms?.livingArea)} m<sup>2</sup>
                          </span>
                        </div>
                        <div>
                          <span className="truncate text-[10px] font-medium leading-[28px] text-[#A0A0A0]">
                            {t('price')}
                          </span>
                          <span className="block truncate text-[10px] font-medium leading-[28px]">
                            {getRenderValue(
                              getPriceWithCurrency(
                                estate.prices?.buyingPrice,
                                estate.prices?.currency,
                                i18n.language
                              )
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="truncate text-[10px] font-medium leading-[28px] text-[#A0A0A0]">
                            {t('author')}
                          </span>
                          <span className="block truncate text-[10px] font-medium leading-[28px]">
                            {getRenderValue(
                              `${getRenderValue(estate.creator?.firstName)} ${getRenderValue(estate.creator?.lastName)}`
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <span>{t('noObjects')}</span>
          )}
        </div>
      </div>
    </div>
  );
};
